import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { ASSETS } from "../lib/api";
import { SmokeParticles, Embers } from "./SmokeParticles";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";

const MAPS_URL = "https://www.google.com/maps/dir/?api=1&destination=41.758925%2C12.266887&destination_name=Provalo+Spiedinificio";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.4 + i * 0.18, duration: 1.1, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

export const Hero = ({ onReserve }) => {
  const { t } = useT();
  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <video
        className="absolute inset-0 w-full h-full object-cover kenburns"
        src={ASSETS.attimosphereVideo}
        poster={ASSETS.picanhaSmoke}
        autoPlay muted loop playsInline preload="auto"
        data-testid="hero-video"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0E0A07]/40 via-transparent to-[#D4612A]/10 pointer-events-none mix-blend-multiply" />
      <SmokeParticles count={5} />
      <Embers count={14} />
      <div className="absolute inset-0 vignette grain" />

      <div className="relative z-20 flex items-start justify-between px-5 md:px-10 pt-6 md:pt-9 gap-3">
        <div className="flex items-center gap-3 no-select">
          <img src={ASSETS.logo} alt="Provalo Spiedinificio" className="h-14 w-14 md:h-16 md:w-16 object-contain" />
          <span className="hidden md:inline font-display italic text-[16px] tracking-wider text-[var(--pn-cream)]/80">
            Provalo Spiedinificio
          </span>
        </div>
        <div className="flex flex-col items-end gap-4 md:gap-5">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-[#C8341E]/30 bg-[#C8341E]/10 backdrop-blur-sm">
              <span className="dot-pulse" />
              <span className="text-[11px] md:text-[13px] tracking-[0.18em] md:tracking-[0.22em] uppercase text-[var(--pn-milk)] font-medium whitespace-nowrap">
                {t.topbar.thursdayOnly}
              </span>
            </div>
            <LanguageSwitcher />
          </div>

          {/* Address pill — right-aligned, under the Thursday badge */}
          <motion.a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
            className="group inline-flex items-center gap-2 md:gap-2.5 px-4 py-2.5 md:px-5 md:py-3 rounded-full border border-[var(--pn-amber)]/30 bg-[var(--pn-black)]/50 backdrop-blur-md hover:border-[var(--pn-amber)]/70 hover:bg-[var(--pn-black)]/70 transition-all duration-500 max-w-[90vw]"
            data-testid="hero-address-link"
            aria-label="Apri Provalo Spiedinificio in Google Maps"
          >
            <MapPin size={15} className="text-[var(--pn-amber)] flex-shrink-0" strokeWidth={1.75} />
            <span className="text-[12.5px] md:text-[14px] tracking-[0.06em] text-[var(--pn-milk)] font-medium truncate">
              <span className="md:hidden">Piazzale E. Molinari · Fiumicino</span>
              <span className="hidden md:inline">Piazzale E. Molinari · Fiumicino (RM)</span>
              <span className="hidden lg:inline text-[var(--pn-cream)]/60 font-light"> — {t.topbar.addressHint}</span>
            </span>
            <ArrowUpRight size={13} className="text-[var(--pn-amber)]/75 group-hover:text-[var(--pn-amber)] transition-colors flex-shrink-0" strokeWidth={1.75} />
          </motion.a>
        </div>
      </div>

      <div className="relative z-10 flex flex-col justify-end min-h-[calc(100svh-90px)] px-6 md:px-14 lg:px-24 pb-24 md:pb-32 max-w-[1320px]">
        <motion.span
          variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="eyebrow mb-6 md:mb-7" data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.span>

        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-display font-light text-[44px] leading-[1.02] sm:text-[60px] md:text-[88px] lg:text-[112px] tracking-[-0.015em] text-[var(--pn-milk)] max-w-[14ch]"
          data-testid="hero-title"
        >
          {t.hero.title[0]}{" "}
          <em className="italic font-light text-[var(--pn-amber)]">{t.hero.title[1]}</em>{" "}
          {t.hero.title[2]}
        </motion.h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-7 md:mt-9 max-w-[620px] text-[16px] md:text-[19px] leading-[1.6] text-[var(--pn-cream)]/85 font-light"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle_a}
          <span className="text-[var(--pn-amber)] italic font-display">{t.hero.subtitle_b}</span>
          {t.hero.subtitle_c}
        </motion.p>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={3}
          className="mt-5 max-w-[560px] text-[13.5px] md:text-[14.5px] leading-[1.6] text-[var(--pn-cream)]/55 italic font-display"
          data-testid="hero-microtext"
        >
          {t.hero.micro}
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href="https://pnssm.pro/5sq02s5"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-primary !text-[18px] md:!text-[21px] !py-[26px] !px-[50px]"
            data-testid="hero-cta-reserve"
          >
            {t.hero.ctaReserve}
            <span aria-hidden>→</span>
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
        transition={{ delay: 2, duration: 1.2 }}
        className="absolute bottom-6 md:bottom-9 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[var(--pn-cream)]/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <div className="w-px h-10 bg-gradient-to-b from-[var(--pn-cream)]/60 to-transparent" />
      </motion.div>
    </section>
  );
};
